import { useState, useEffect } from "react";
import type { ReactNode } from "react";
import { NavLink, Link } from "react-router-dom";
import { useLauncher } from "../services/launcher/useLauncher";
import { WalletButton } from "./WalletButton";
import { ChainSelector } from "./ChainSelector";

const BETA_BANNER_KEY = "synapz_beta_banner_dismissed_v1";

/* ── Top ticker items ─────────────────────────────────────────────────────── */
const TICKER_ITEMS = [
  { icon: "⚡", text: "LIVE ON ROBINHOOD TESTNET" },
  { icon: "◆", text: "FAIR LAUNCH BETA" },
  { icon: "⚙", text: "AI LAUNCH STUDIO" },
  { icon: "◇", text: "BASE — COMING NEXT" },
  { icon: "◇", text: "SOLANA — PLANNED" },
  { icon: "✓", text: "VERIFIED FACTORY CONTRACT" },
  { icon: "⚡", text: "MULTICHAIN ROADMAP" },
  { icon: "◆", text: "BONDING CURVE MARKETS LIVE" },
  { icon: "◇", text: "TRANSPARENT ON-CHAIN LAUNCH" },
  { icon: "✓", text: "NON-CUSTODIAL · ALWAYS" },
];

/* ── Nav links ────────────────────────────────────────────────────────────── */
const NAV = [
  { to: "/launch",         label: "Launch" },
  { to: "/fair-launch",    label: "Markets" },
  { to: "/launch-studio",  label: "AI Studio" },
  { to: "/my-launches",    label: "My Launches" },
  { to: "/networks",       label: "Chains" },
  { to: "/partners/ccc",   label: "CCC Members" },
  { to: "/about",          label: "About" },
];

/* ── Icons ────────────────────────────────────────────────────────────────── */
function LightningIcon() {
  return (
    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <path d="M9.5 1L3 9.5h5.5L6 15l8-8.5H8.5L9.5 1z" />
    </svg>
  );
}

function MenuIcon({ open }: { open: boolean }) {
  return (
    <svg aria-hidden="true" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
      {open ? (
        <>
          <line x1="4" y1="4" x2="16" y2="16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <line x1="16" y1="4" x2="4" y2="16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </>
      ) : (
        <>
          <rect x="3" y="5" width="14" height="1.8" rx="0.9" />
          <rect x="3" y="9.1" width="14" height="1.8" rx="0.9" />
          <rect x="3" y="13.2" width="14" height="1.8" rx="0.9" />
        </>
      )}
    </svg>
  );
}

/* ── Ticker strip ─────────────────────────────────────────────────────────── */
function TickerStrip() {
  // Double the items so the CSS loop is seamless
  const doubled = [...TICKER_ITEMS, ...TICKER_ITEMS];
  return (
    <div className="ticker-outer border-b border-line/40 bg-deep/80 py-1.5 overflow-hidden">
      <div className="ticker-track">
        {doubled.map((item, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-1.5 px-6 text-[10px] font-mono font-medium tracking-widest uppercase"
          >
            <span className="text-plasma/70">{item.icon}</span>
            <span className="text-muted/60">{item.text}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── Logo ─────────────────────────────────────────────────────────────────── */
function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2.5 shrink-0 group" aria-label="SYNAPZ home">
      {/* Lightning bolt mark */}
      <span className="flex items-center justify-center w-7 h-7 rounded-md bg-gradient-to-br from-ultra-violet/80 to-magenta/60 text-white shadow-glow-violet group-hover:shadow-glow-magenta transition-shadow duration-300">
        <LightningIcon />
      </span>
      <span className="font-display text-[18px] font-bold tracking-tight text-ink">
        SYNAPZ
        <span className="text-plasma ml-[2px]">·</span>
      </span>
      <span className="hidden sm:inline text-[9px] text-muted/50 font-mono tracking-widest uppercase leading-none pt-[1px]">
        Multichain<br />Launchpad
      </span>
    </Link>
  );
}

/* ── Live chip ────────────────────────────────────────────────────────────── */
function LiveChip() {
  return (
    <span className="hidden lg:inline-flex items-center gap-1.5 rounded-full border border-plasma/25 bg-plasma/8 px-2.5 py-1 text-[10px] font-mono font-semibold text-plasma/80 tracking-wide"
      style={{ background: "rgba(0,229,199,0.06)" }}>
      <span className="block w-1.5 h-1.5 rounded-full bg-plasma chain-dot-live animate-chain-pulse" />
      TESTNET LIVE
    </span>
  );
}

/* ── Beta disclosure banner (dismissible, first-visit only) ───────────────── */
function BetaBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!sessionStorage.getItem(BETA_BANNER_KEY) && !localStorage.getItem(BETA_BANNER_KEY)) {
      setVisible(true);
    }
  }, []);

  function dismiss(permanent: boolean) {
    if (permanent) localStorage.setItem(BETA_BANNER_KEY, "1");
    else sessionStorage.setItem(BETA_BANNER_KEY, "1");
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      role="alert"
      aria-live="polite"
      className="border-b border-warn/25 px-4 py-3 text-xs leading-relaxed"
      style={{ background: "rgba(255,176,32,0.07)" }}
    >
      <div className="mx-auto max-w-7xl flex items-start gap-3">
        <svg aria-hidden="true" className="shrink-0 mt-0.5 text-warn/70" width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
          <path d="M7 1a6 6 0 110 12A6 6 0 017 1zm0 3.5a.75.75 0 00-.75.75v2.5a.75.75 0 001.5 0v-2.5A.75.75 0 007 4.5zm0 5.25a.75.75 0 100-1.5.75.75 0 000 1.5z" />
        </svg>
        <p className="flex-1 text-warn/80">
          <strong className="font-semibold text-warn">Unaudited Beta — use at your own risk.</strong>{" "}
          Smart contracts have not been independently audited. Do not commit funds you cannot afford to lose.{" "}
          <Link to="/legal/unaudited-beta" className="underline underline-offset-2 hover:text-warn transition-colors">Beta disclosure</Link>
          {" · "}
          <Link to="/risk" className="underline underline-offset-2 hover:text-warn transition-colors">Risk disclosure</Link>
        </p>
        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={() => dismiss(false)}
            className="text-warn/60 hover:text-warn transition-colors text-[11px] font-mono underline underline-offset-2"
            aria-label="Dismiss for this session"
          >
            Dismiss
          </button>
          <button
            onClick={() => dismiss(true)}
            className="text-muted/40 hover:text-muted/70 transition-colors"
            aria-label="Close and don't show again"
          >
            <svg aria-hidden="true" width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M3 3l8 8M11 3l-8 8" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Layout ───────────────────────────────────────────────────────────────── */
export function Layout({ children }: { children: ReactNode }) {
  const { isDemo, isE2E, configError } = useLauncher();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-full flex flex-col">
      {/* Skip link */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded-lg focus:bg-plasma focus:px-4 focus:py-1.5 focus:text-void focus:font-semibold focus:text-sm"
      >
        Skip to content
      </a>

      {/* E2E mock banner */}
      {isE2E && (
        <div role="alert" className="bg-danger text-white text-center text-xs font-bold px-3 py-2 tracking-wide">
          E2E MOCK ENVIRONMENT — NO BLOCKCHAIN TRANSACTIONS ARE REAL
        </div>
      )}

      {/* Independence disclaimer */}
      <div className="border-b border-line/50 bg-void/95 text-center text-[11px] text-muted/60 px-4 py-1.5 leading-relaxed">
        Independent token creation tool for Robinhood Chain.{" "}
        <Link to="/legal/non-affiliation" className="text-muted/40 hover:text-muted/60 transition-colors underline underline-offset-2">
          Not affiliated with or endorsed by Robinhood.
        </Link>
      </div>

      {/* Dismissible beta / risk disclosure banner */}
      <BetaBanner />

      {/* Config error */}
      {configError && (
        <div role="alert" className="border-b border-danger/40 bg-danger/10 text-center text-xs text-danger px-3 py-2">
          Configuration error — {configError.field}: {configError.message} Deployment is disabled.
        </div>
      )}

      {/* Demo banner */}
      {isDemo && !configError && (
        <div
          role="status"
          className="border-b border-warn/20 text-center text-xs text-warn/80 px-3 py-1.5"
          style={{ background: "rgba(255,176,32,0.05)" }}
        >
          <span className="inline-flex items-center gap-1.5">
            <svg aria-hidden="true" width="11" height="11" viewBox="0 0 12 12" fill="currentColor">
              <path d="M6 1a5 5 0 110 10A5 5 0 016 1zm0 3.5a.75.75 0 00-.75.75v2.5a.75.75 0 001.5 0v-2.5A.75.75 0 006 4.5zm0 5.25a.75.75 0 100-1.5.75.75 0 000 1.5z" />
            </svg>
            Testnet factory not deployed — blockchain deployment currently disabled.
          </span>
        </div>
      )}

      {/* Ticker strip */}
      <TickerStrip />

      {/* Sticky header */}
      <header className="sticky top-0 z-20 border-b border-line/50 bg-void/90 backdrop-blur-md"
        style={{ boxShadow: "0 1px 0 rgba(157,0,255,0.05), 0 4px 24px rgba(0,0,0,0.35)" }}>
        <div className="mx-auto max-w-7xl px-4 py-3 flex items-center justify-between gap-4">
          <Logo />

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-0.5 flex-1 justify-center" aria-label="Main navigation">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `relative rounded-md px-3.5 py-1.5 text-[13px] font-medium transition-colors duration-150 ${
                    isActive
                      ? "text-plasma"
                      : "text-muted hover:text-ink"
                  }`
                }
                style={({ isActive }) =>
                  isActive
                    ? { background: "rgba(0,229,199,0.07)", boxShadow: "0 0 0 1px rgba(0,229,199,0.12)" }
                    : {}
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2 shrink-0">
            <LiveChip />
            <ChainSelector />
            <WalletButton />
            <button
              className="md:hidden p-2 rounded-md text-muted hover:text-ink hover:bg-line/50 transition-colors"
              onClick={() => setMobileOpen((o) => !o)}
              aria-label="Toggle navigation"
              aria-expanded={mobileOpen}
            >
              <MenuIcon open={mobileOpen} />
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        {mobileOpen && (
          <nav className="md:hidden border-t border-line/50 bg-deep/95 backdrop-blur-md" aria-label="Mobile navigation">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center px-5 py-3.5 text-sm border-b border-line/30 transition-colors ${
                    isActive ? "text-plasma bg-plasma/5" : "text-muted hover:text-ink hover:bg-line/20"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
        )}
      </header>

      {/* Main content — no max-width constraint at layout level; pages control it */}
      <main id="main" className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-line/50 mt-auto"
        style={{ background: "rgba(5,6,10,0.95)" }}>
        <div className="mx-auto max-w-7xl px-4 py-8 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="space-y-1 max-w-md">
              <div className="flex items-center gap-2">
                <span className="font-display text-sm font-bold text-ink">SYNAPZ</span>
                <span className="text-[10px] font-mono text-muted/50 tracking-widest uppercase border border-line/60 rounded px-1.5 py-0.5">TESTNET BETA</span>
              </div>
              <p className="text-xs text-muted/60 leading-relaxed">
                The multichain token launchpad. Create, discover and trade tokens across supported blockchains with transparent on-chain infrastructure.
              </p>
              <p className="text-xs text-muted/50 leading-relaxed">
                Creating a token on Robinhood Chain does not list it in the Robinhood trading application.
              </p>
            </div>
            <div className="flex flex-col gap-1.5">
              <p className="text-[10px] font-mono text-muted/40 uppercase tracking-wide">Networks</p>
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2 text-xs text-muted/60">
                  <span className="w-1.5 h-1.5 rounded-full chain-dot-live" />
                  Robinhood Chain — Testnet Live
                </div>
                <div className="flex items-center gap-2 text-xs text-muted/40">
                  <span className="w-1.5 h-1.5 rounded-full chain-dot-next" />
                  Base — Coming Next
                </div>
                <div className="flex items-center gap-2 text-xs text-muted/40">
                  <span className="w-1.5 h-1.5 rounded-full chain-dot-soon" />
                  Solana — Planned
                </div>
              </div>
            </div>
          </div>

          <div className="h-px bg-line/40" />

          {/* Legal links — four columns on desktop, two on mobile */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-4">
            <div className="space-y-2">
              <p className="text-[9px] font-mono font-semibold text-muted/35 uppercase tracking-widest">Agreements</p>
              {[
                { to: "/terms",          label: "Terms of Service" },
                { to: "/privacy",        label: "Privacy Policy" },
                { to: "/acceptable-use", label: "Acceptable Use" },
              ].map(({ to, label }) => (
                <NavLink key={to} to={to} className="block text-xs text-muted/50 hover:text-ink transition-colors">
                  {label}
                </NavLink>
              ))}
            </div>
            <div className="space-y-2">
              <p className="text-[9px] font-mono font-semibold text-muted/35 uppercase tracking-widest">Risk</p>
              {[
                { to: "/risk",                            label: "Risk Disclosure" },
                { to: "/legal/unaudited-beta",            label: "Unaudited Beta" },
                { to: "/legal/smart-contract-risk",       label: "Smart Contract Risk" },
                { to: "/legal/irreversible-transactions", label: "Irreversible Transactions" },
                { to: "/legal/no-guaranteed-returns",     label: "No Guaranteed Returns" },
              ].map(({ to, label }) => (
                <NavLink key={to} to={to} className="block text-xs text-muted/50 hover:text-ink transition-colors">
                  {label}
                </NavLink>
              ))}
            </div>
            <div className="space-y-2">
              <p className="text-[9px] font-mono font-semibold text-muted/35 uppercase tracking-widest">Creator</p>
              {[
                { to: "/creator-rewards",                        label: "Creator Rewards" },
                { to: "/legal/creator-recipient-immutability",   label: "Recipient Immutability" },
                { to: "/legal/contract-addresses",               label: "Contract Addresses" },
              ].map(({ to, label }) => (
                <NavLink key={to} to={to} className="block text-xs text-muted/50 hover:text-ink transition-colors">
                  {label}
                </NavLink>
              ))}
            </div>
            <div className="space-y-2">
              <p className="text-[9px] font-mono font-semibold text-muted/35 uppercase tracking-widest">Platform</p>
              {[
                { to: "/legal/content-moderation", label: "Content Moderation" },
                { to: "/legal/incident-response",  label: "Incident Response" },
                { to: "/legal/non-affiliation",    label: "Non-Affiliation Notice" },
                { to: "/legal",                    label: "All Legal Docs →" },
              ].map(({ to, label }) => (
                <NavLink key={to} to={to} className="block text-xs text-muted/50 hover:text-ink transition-colors">
                  {label}
                </NavLink>
              ))}
            </div>
          </div>

          <div className="h-px bg-line/40" />

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-[11px] text-muted/35 font-mono">
              Unaudited testnet beta · Smart contracts not independently audited · Use at your own risk
            </p>
            <NavLink to="/admin/team-access" className="text-[11px] text-muted/25 hover:text-muted/50 font-mono transition-colors">
              Team
            </NavLink>
          </div>
        </div>
      </footer>
    </div>
  );
}
